import {
  Ban,
  CircleCheck,
  CircleDashed,
  LoaderCircle,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

/**
 * Tones are semantic, not decorative. `danger`/`warning`/`success` resolve to
 * the platform colours, which are deliberately not brandable (§11.6) — a
 * tenant cannot make a failure look like a success.
 */
export type BadgeTone = 'neutral' | 'progress' | 'success' | 'warning' | 'error';

/**
 * §16.4. Every pill is a rounded, bordered chip drawing only from the platform
 * semantic palette — `--radius-pill` is deliberately NOT derived from the
 * tenant's radius scale, because a square status pill reads as a table cell
 * rather than a state.
 */

const TONE_CLASS: Record<BadgeTone, string> = {
  neutral: 'bg-surface-alt text-muted border border-line',
  // §16.4: `info`, not `primary`. A pill must survive a tenant whose primary is
  // red or green, so it draws only from the platform palette — and the text
  // token is the one measured against the soft background, not the fill.
  progress: 'bg-info-soft text-info-text border border-info/20',
  success: 'bg-success-soft text-success-text border border-success/20',
  warning: 'bg-warning-soft text-warning-text border border-warning/20',
  error: 'bg-danger-soft text-danger-text border border-danger/20',
};

/**
 * One icon per tone, so state is carried by **shape as well as hue**.
 *
 * This matters twice over. WCAG 1.4.1 says colour is never the only channel —
 * the label already covered that, and the icon makes it legible at a glance
 * rather than only on reading. And the platform's own `warning` (#b54708) and
 * `error` (#b42318) measure ΔE 6.1 apart for *normal* colour vision, well under
 * the 15 the palette validator wants: side by side they are genuinely hard to
 * tell apart by hue. The glyphs are what separate them.
 */
const TONE_ICON: Record<BadgeTone, LucideIcon> = {
  neutral: CircleDashed,
  progress: LoaderCircle,
  success: CircleCheck,
  warning: TriangleAlert,
  error: Ban,
};

/**
 * A pill is a **mark**, not a control, and the two are told apart by weight.
 *
 * `md` is the default and is one step tighter than Phase 05 shipped: 11px text
 * in a 2px-tall box. `sm` exists for a pill living inside a 40px table row,
 * where anything with real padding reads as a button an operator can press —
 * and a status that looks pressable in an estate of a thousand SIMs is an
 * invitation to click it.
 */
export type BadgeSize = 'sm' | 'md';

/**
 * The horizontal padding is the size ladder; the vertical is what keeps both
 * rungs a mark rather than a control.
 *
 * `sm` was 4px of side padding, which on a `--radius-pill` chip is less than
 * the cap's own curve — the first and last glyph of a status sat inside the
 * rounded end and the pill read as clipped. 8px clears the cap. `md` moves up
 * to 12px so the two are still visibly different objects, which they have to be
 * now that `sm` is the pill every table row uses.
 */
const SIZE_CLASS: Record<BadgeSize, string> = {
  sm: 'gap-1 px-[var(--space-2)] py-0 text-[length:var(--text-2xs)]',
  md: 'gap-1 px-[var(--space-3)] py-[2px] text-[length:var(--text-2xs)]',
};

/** Kept in step with the text size: a glyph taller than its label is a button. */
const SIZE_ICON: Record<BadgeSize, number> = { sm: 10, md: 11 };

export function Badge({
  tone = 'neutral',
  size = 'md',
  children,
}: {
  tone?: BadgeTone;
  /** Defaults to the standalone pill; `sm` is for a pill inside a table row. */
  size?: BadgeSize;
  children: ReactNode;
}): ReactElement {
  const Icon = TONE_ICON[tone];
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-[var(--radius-pill)] font-medium leading-[1.4] ${SIZE_CLASS[size]} ${TONE_CLASS[tone]}`}
    >
      {/* Decorative: the pill's own text is the accessible name. */}
      <Icon aria-hidden="true" size={SIZE_ICON[size]} strokeWidth={2.25} className="shrink-0" />
      {children}
    </span>
  );
}
